import { apiService } from './api';

// Types for authentication
export interface LoginCredentials {
    email: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    refresh_token: string;
    expires_in: number;
}

export interface RefreshTokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
}

export interface EsqueciMinhaSenhaRequest {
    email: string;
}

export interface ValidarTokenRecuperacaoResponse {
    valid: boolean;
    email?: string;
    nome?: string;
    message?: string;
}

// Token storage keys
const REFRESH_TOKEN_STORAGE_KEY = '@KatruFront:refresh_token';
const TOKEN_EXPIRY_STORAGE_KEY = '@KatruFront:token_expiry';

// Auth Service class
export class AuthService {
    private static instance: AuthService;
    private refreshPromise: Promise<RefreshTokenResponse> | null = null;

    private constructor() { }

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    // Check if token is expired or about to expire (within 5 minutes)
    private isTokenExpired(): boolean {
        const expiryTime = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);
        if (!expiryTime) return true;

        const expiry = parseInt(expiryTime);
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

        return now >= (expiry - fiveMinutes);
    }

    // Set token expiry time
    private setTokenExpiry(expiresIn: number): void {
        const expiryTime = Date.now() + (expiresIn * 1000);
        localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiryTime.toString());
    }

    // Get token expiry time
    private getTokenExpiry(): number | null {
        const expiryTime = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);
        return expiryTime ? parseInt(expiryTime) : null;
    }

    // Ensure valid token (refresh if needed)
    async ensureValidToken(): Promise<string | null> {
        const currentToken = apiService.getAuthToken();

        if (!currentToken) {
            return null;
        }

        if (this.isTokenExpired()) {
            try {
                const refreshResponse = await this.refreshToken();
                return refreshResponse.access_token;
            } catch (error) {
                console.error('Failed to refresh token:', error);
                await this.logout();
                return null;
            }
        }

        return currentToken;
    }

    // Login user
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        try {
            // Convert credentials to FormData
            const formData = new FormData();
            formData.append('username', credentials.email);
            formData.append('password', credentials.password);

            const response = await apiService.post<LoginResponse>('/auth/token', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.access_token) {
                // Store the access token using apiService method
                apiService.setAuthToken(response.access_token);
                localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refresh_token);

                // Set token expiry
                this.setTokenExpiry(response.expires_in);

                return response;
            } else {
                throw new Error('Login failed');
            }
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    // Logout user
    async logout(): Promise<void> {
        try {
            // Clear tokens using apiService methods
            apiService.removeAuthToken();
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
        } catch (error) {
            console.error('Logout error:', error);
            // Even if there's an error, still clear tokens and redirect
            apiService.removeAuthToken();
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
        } finally {
            window.location.href = '/login';
        }
    }

    // Refresh access token with request deduplication
    async refreshToken(): Promise<RefreshTokenResponse> {
        // If there's already a refresh in progress, return the existing promise
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.performTokenRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.refreshPromise = null;
        }
    }

    private async performTokenRefresh(): Promise<RefreshTokenResponse> {
        try {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await apiService.post<LoginResponse>('/auth/refresh-token', {
                refresh_token: refreshToken
            });

            if (response.access_token) {
                // Update the access token using apiService method
                apiService.setAuthToken(response.access_token);

                // Update the refresh token
                localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refresh_token);

                // Set new token expiry
                this.setTokenExpiry(response.expires_in);

                return {
                    access_token: response.access_token,
                    expires_in: response.expires_in,
                    token_type: 'Bearer'
                };
            } else {
                throw new Error('Token refresh failed');
            }
        } catch (error) {
            // If refresh fails, logout the user
            await this.logout();
            throw error;
        }
    }


    // Get access token using apiService method
    getAccessToken(): string | null {
        return apiService.getAuthToken();
    }

    // Get token expiry information
    getTokenExpiryInfo(): { isExpired: boolean; expiresAt: Date | null; timeUntilExpiry: number | null } {
        const expiryTime = this.getTokenExpiry();
        if (!expiryTime) {
            return { isExpired: true, expiresAt: null, timeUntilExpiry: null };
        }

        const now = Date.now();
        const isExpired = now >= expiryTime;
        const timeUntilExpiry = isExpired ? null : expiryTime - now;

        return {
            isExpired,
            expiresAt: new Date(expiryTime),
            timeUntilExpiry
        };
    }

    // Update password using magic link token
    async atualizarSenhaComToken(token: string, newPassword: string): Promise<void> {
        try {
            const requestBody = {
                token: token,
                newPassword: newPassword
            };

            await apiService.post('/auth/update-password', requestBody);
        } catch (error) {
            console.error('Update password error:', error);
            throw error;
        }
    }

    // Request password recovery
    async esqueciMinhaSenha(email: string): Promise<void> {
        try {
            const requestBody: EsqueciMinhaSenhaRequest = {
                email: email
            };

            await apiService.post('/auth/esqueci-minha-senha', requestBody);
        } catch (error) {
            console.error('Password recovery request error:', error);
            throw error;
        }
    }

    // Validate recovery token
    async validarTokenRecuperacao(token: string): Promise<ValidarTokenRecuperacaoResponse> {
        try {
            const response = await apiService.post<ValidarTokenRecuperacaoResponse>(`/auth/validar-token-recuperacao?token=${token}`);
            return response;
        } catch (error) {
            console.error('Token validation error:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const authService = AuthService.getInstance();

// Export default for convenience
export default authService; 